import React, { useState, useEffect, useCallback } from "react";
import { Calendar, Clock, Users, Plus, Trash2, Check, X, ChevronLeft, ChevronRight, Package, DollarSign, AlertTriangle, UserCheck, Settings, ClipboardCheck, Bell, Send } from "lucide-react";
import { toast } from "sonner";

const API = "/api";
const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
const CATEGORIES = [
  { value: "1-on-1", label: "1-on-1" },
  { value: "small-group", label: "Small Group" },
  { value: "class", label: "Class" },
  { value: "custom", label: "Custom" },
];

function generateTimeSlots() {
  const slots = [];
  for (let h = 5; h < 22; h++) {
    for (let m = 0; m < 60; m += 30) {
      const hh = h.toString().padStart(2, "0");
      const mm = m.toString().padStart(2, "0");
      slots.push(`${hh}:${mm}`);
    }
  }
  return slots;
}
const TIME_SLOTS = generateTimeSlots();

const formatTime = (t) => {
  if (!t) return "";
  const [h, m] = t.split(":");
  const hr = parseInt(h);
  const ampm = hr >= 12 ? "PM" : "AM";
  const hr12 = hr === 0 ? 12 : hr > 12 ? hr - 12 : hr;
  return `${hr12}:${m} ${ampm}`;
};

// ─── Sub-tab: Session Types ───
const SessionTypesManager = ({ sessionTypes, onRefresh }) => {
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: "", category: "1-on-1", duration_minutes: 60, capacity: 1, price: 0, description: "" });

  const save = async () => {
    if (!form.name) return toast.error("Name required");
    const res = await fetch(`${API}/schedule/session-types`, { method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include", body: JSON.stringify(form) });
    if (res.ok) { toast.success("Session type created"); setShowForm(false); setForm({ name: "", category: "1-on-1", duration_minutes: 60, capacity: 1, price: 0, description: "" }); onRefresh(); }
  };

  const remove = async (id) => {
    await fetch(`${API}/schedule/session-types/${id}`, { method: "DELETE", credentials: "include" });
    toast.success("Deleted"); onRefresh();
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-lg">Session Types</h3>
        <button onClick={() => setShowForm(!showForm)} className="flex items-center gap-2 bg-primary text-white px-4 py-2 rounded-lg text-sm font-medium" data-testid="add-session-type-btn">
          <Plus className="w-4 h-4" /> Add Type
        </button>
      </div>

      {showForm && (
        <div className="bg-card border border-border rounded-xl p-4 space-y-3" data-testid="session-type-form">
          <input placeholder="Session name (e.g., Personal Training)" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full bg-[#0B0F1A] border border-border rounded-lg px-3 py-2 text-white text-sm" data-testid="session-type-name" />
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="bg-[#0B0F1A] border border-border rounded-lg px-3 py-2 text-white text-sm">
              {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
            </select>
            <input type="number" placeholder="Duration (min)" value={form.duration_minutes} onChange={(e) => setForm({ ...form, duration_minutes: parseInt(e.target.value) || 60 })} className="bg-[#0B0F1A] border border-border rounded-lg px-3 py-2 text-white text-sm" />
            <input type="number" placeholder="Capacity" value={form.capacity} onChange={(e) => setForm({ ...form, capacity: parseInt(e.target.value) || 1 })} className="bg-[#0B0F1A] border border-border rounded-lg px-3 py-2 text-white text-sm" />
            <input type="number" placeholder="Price ($)" value={form.price} onChange={(e) => setForm({ ...form, price: parseFloat(e.target.value) || 0 })} className="bg-[#0B0F1A] border border-border rounded-lg px-3 py-2 text-white text-sm" />
          </div>
          <textarea placeholder="Description (optional)" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="w-full bg-[#0B0F1A] border border-border rounded-lg px-3 py-2 text-white text-sm h-16 resize-none" />
          <div className="flex gap-2">
            <button onClick={save} className="bg-primary text-white px-4 py-2 rounded-lg text-sm font-medium" data-testid="save-session-type">Save</button>
            <button onClick={() => setShowForm(false)} className="bg-[#1E293B] text-gray-400 px-4 py-2 rounded-lg text-sm">Cancel</button>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {sessionTypes.map((st) => (
          <div key={st.type_id} className="bg-card border border-border rounded-xl p-4 flex items-start justify-between" data-testid={`session-type-${st.type_id}`}>
            <div>
              <p className="font-semibold text-white">{st.name}</p>
              <div className="flex items-center gap-3 mt-1 text-xs text-text-secondary">
                <span className="bg-primary/20 text-primary px-2 py-0.5 rounded-full">{st.category}</span>
                <span><Clock className="w-3 h-3 inline mr-1" />{st.duration_minutes} min</span>
                <span><Users className="w-3 h-3 inline mr-1" />{st.capacity}</span>
                {st.price > 0 && <span className="text-emerald-400">${st.price}</span>}
              </div>
              {st.description && <p className="text-xs text-text-muted mt-1">{st.description}</p>}
            </div>
            <button onClick={() => remove(st.type_id)} className="text-red-400/50 hover:text-red-400 p-1"><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
        {sessionTypes.length === 0 && <p className="text-text-muted text-sm col-span-2">No session types yet. Create your first one above.</p>}
      </div>
    </div>
  );
};

// ─── Sub-tab: Availability ───
const AvailabilityManager = ({ slots, sessionTypes, onRefresh }) => {
  const [selectedDate, setSelectedDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [startTime, setStartTime] = useState("09:00");
  const [endTime, setEndTime] = useState("09:30");
  const [sessionTypeId, setSessionTypeId] = useState("");
  const [capacity, setCapacity] = useState(1);
  const [isRecurring, setIsRecurring] = useState(false);
  const [recurringWeeks, setRecurringWeeks] = useState(4);
  const [weekOffset, setWeekOffset] = useState(0);
  const [bulkTarget, setBulkTarget] = useState(null);

  const getSlotPayload = (date, st, et) => ({
    date, start_time: st, end_time: et,
    session_type_id: sessionTypeId || null,
    capacity,
    is_recurring: isRecurring,
    recurrence_day: isRecurring ? new Date(date + "T12:00").getDay() : null,
  });

  const addSlot = async () => {
    const res = await fetch(`${API}/schedule/availability`, {
      method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
      body: JSON.stringify({ ...getSlotPayload(selectedDate, startTime, endTime), recurring_weeks: recurringWeeks })
    });
    if (res.ok) { const d = await res.json(); toast.success(`${d.count} slot${d.count !== 1 ? "s" : ""} created`); onRefresh(); }
  };

  const addDaySlots = async () => {
    const slotsToCreate = [];
    let current = startTime;
    while (current < endTime) {
      const [h, m] = current.split(":").map(Number);
      const nextM = m + 30;
      const nextH = nextM >= 60 ? h + 1 : h;
      const next = `${String(nextH).padStart(2, "0")}:${String(nextM % 60).padStart(2, "0")}`;
      slotsToCreate.push(getSlotPayload(selectedDate, current, next));
      current = next;
    }
    if (slotsToCreate.length === 0) return toast.error("End time must be after start time");
    const res = await fetch(`${API}/schedule/availability/bulk`, { method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include", body: JSON.stringify({ slots: slotsToCreate, recurring_weeks: recurringWeeks }) });
    if (res.ok) { const data = await res.json(); toast.success(`${data.count} slots created`); onRefresh(); }
  };

  const bulkAction = async (slotId, action, scope) => {
    const res = await fetch(`${API}/schedule/availability/bulk-action`, {
      method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
      body: JSON.stringify({ slot_id: slotId, action, scope }),
    });
    if (res.ok) {
      const data = await res.json();
      toast.success(`${data.affected} slot${data.affected !== 1 ? "s" : ""} removed`);
      setBulkTarget(null);
      onRefresh();
    }
  };

  const makeRecurring = async (slotId, weeks = 4) => {
    const res = await fetch(`${API}/schedule/availability/${slotId}/make-recurring`, {
      method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
      body: JSON.stringify({ weeks }),
    });
    if (res.ok) {
      const data = await res.json();
      toast.success(`Slot is now recurring — ${data.created} future week${data.created !== 1 ? "s" : ""} created`);
      setBulkTarget(null);
      onRefresh();
    }
  };

  const getWeekDates = () => {
    const today = new Date();
    today.setDate(today.getDate() + weekOffset * 7 - today.getDay() + 1);
    return Array.from({ length: 7 }, (_, i) => {
      const d = new Date(today);
      d.setDate(d.getDate() + i);
      return d.toISOString().slice(0, 10);
    });
  };
  const weekDates = getWeekDates();
  const slotsByDate = {};
  slots.forEach(s => { if (!slotsByDate[s.date]) slotsByDate[s.date] = []; slotsByDate[s.date].push(s); });
  const stMap = {};
  sessionTypes.forEach(st => { stMap[st.type_id] = st; });

  return (
    <div className="space-y-6">
      {/* Add slots form */}
      <div className="bg-card border border-border rounded-xl p-4 space-y-3">
        <h4 className="font-semibold text-sm text-white">Add Availability</h4>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          <input type="date" value={selectedDate} onChange={(e) => setSelectedDate(e.target.value)} className="bg-[#0B0F1A] border border-border rounded-lg px-3 py-2 text-white text-sm" data-testid="avail-date" />
          <select value={startTime} onChange={(e) => setStartTime(e.target.value)} className="bg-[#0B0F1A] border border-border rounded-lg px-3 py-2 text-white text-sm">
            {TIME_SLOTS.map(t => <option key={t} value={t}>{formatTime(t)}</option>)}
          </select>
          <select value={endTime} onChange={(e) => setEndTime(e.target.value)} className="bg-[#0B0F1A] border border-border rounded-lg px-3 py-2 text-white text-sm">
            {TIME_SLOTS.map(t => <option key={t} value={t}>{formatTime(t)}</option>)}
          </select>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          <select value={sessionTypeId} onChange={(e) => setSessionTypeId(e.target.value)} className="bg-[#0B0F1A] border border-border rounded-lg px-3 py-2 text-white text-sm" data-testid="avail-session-type">
            <option value="">No specific type</option>
            {sessionTypes.map(st => <option key={st.type_id} value={st.type_id}>{st.name} ({st.category})</option>)}
          </select>
          <div className="flex items-center gap-2">
            <label className="text-xs text-gray-400 whitespace-nowrap">Spots:</label>
            <input type="number" min={1} max={50} value={capacity} onChange={(e) => setCapacity(Math.max(1, parseInt(e.target.value) || 1))}
              className="bg-[#0B0F1A] border border-border rounded-lg px-3 py-2 text-white text-sm w-20" data-testid="avail-capacity" />
          </div>
          <div className="flex items-center gap-3">
            <label className="flex items-center gap-2 text-sm text-white cursor-pointer">
              <input type="checkbox" checked={isRecurring} onChange={(e) => setIsRecurring(e.target.checked)} className="rounded" data-testid="avail-recurring" /> Recurring
            </label>
            {isRecurring && (
              <select value={recurringWeeks} onChange={(e) => setRecurringWeeks(parseInt(e.target.value))}
                className="bg-[#0B0F1A] border border-border rounded-lg px-2 py-1.5 text-white text-xs" data-testid="recurring-weeks">
                {[2, 4, 6, 8, 12].map(w => <option key={w} value={w}>{w} weeks</option>)}
              </select>
            )}
          </div>
        </div>
        <div className="flex gap-2">
          <button onClick={addSlot} className="bg-primary text-white px-4 py-2 rounded-lg text-sm font-medium" data-testid="add-single-slot"><Plus className="w-4 h-4 inline mr-1" />Add Single Slot</button>
          <button onClick={addDaySlots} className="bg-emerald-600 text-white px-4 py-2 rounded-lg text-sm font-medium" data-testid="add-range-slots"><Plus className="w-4 h-4 inline mr-1" />Add Range (30min each)</button>
        </div>
      </div>

      {/* Week calendar */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <button onClick={() => setWeekOffset(w => w - 1)} className="p-2 rounded-lg bg-[#1E293B] hover:bg-[#2D3B4F]"><ChevronLeft className="w-4 h-4" /></button>
          <span className="text-sm font-medium text-white">{weekDates[0]} — {weekDates[6]}</span>
          <button onClick={() => setWeekOffset(w => w + 1)} className="p-2 rounded-lg bg-[#1E293B] hover:bg-[#2D3B4F]"><ChevronRight className="w-4 h-4" /></button>
        </div>
        <div className="grid grid-cols-7 gap-3">
          {weekDates.map((date, i) => {
            const daySlots = (slotsByDate[date] || []).sort((a, b) => a.start_time.localeCompare(b.start_time));
            const isToday = date === new Date().toISOString().slice(0, 10);
            return (
              <div key={date} className={`bg-card border rounded-xl p-3 min-h-[180px] ${isToday ? "border-primary/50" : "border-border"}`}>
                <p className={`text-sm font-bold mb-0.5 ${isToday ? "text-primary" : "text-white/80"}`}>{DAYS[i]}</p>
                <p className="text-xs text-white/40 mb-3">{date.slice(5)}</p>
                <div className="space-y-1.5">
                  {daySlots.map(s => {
                    const st = stMap[s.session_type_id];
                    const cap = s.capacity || 1;
                    const conf = s.confirmed_count || 0;
                    const isFull = conf >= cap;
                    return (
                      <div key={s.slot_id} className="relative group" data-testid={`coach-slot-${s.slot_id}`}>
                        <div className={`flex items-center justify-between px-2 py-1.5 rounded-lg text-xs font-medium ${
                          isFull ? "bg-red-500/15 text-red-400" : s.status === "booked" ? "bg-amber-500/20 text-amber-400" : "bg-emerald-500/15 text-emerald-400"
                        }`}>
                          <div className="flex-1 min-w-0">
                            <span>{formatTime(s.start_time)}</span>
                            {st && <span className="text-[9px] text-gray-500 ml-1">{st.name}</span>}
                          </div>
                          <div className="flex items-center gap-1 flex-shrink-0">
                            <span className={`text-[9px] font-bold ${isFull ? "text-red-400" : conf > 0 ? "text-amber-400" : "text-gray-500"}`}>
                              {conf}/{cap}
                            </span>
                            <button onClick={() => setBulkTarget(bulkTarget === s.slot_id ? null : s.slot_id)} className="opacity-0 group-hover:opacity-100 hover:bg-white/10 p-0.5 rounded transition-opacity" data-testid={`slot-menu-${s.slot_id}`}>
                              <Settings className="w-3 h-3 text-gray-400" />
                            </button>
                          </div>
                        </div>
                        {bulkTarget === s.slot_id && (
                          <div className="absolute right-0 top-full mt-1 z-20 bg-[#1E293B] border border-border rounded-xl shadow-xl p-2 w-56 space-y-1" data-testid={`bulk-menu-${s.slot_id}`}>
                            {!s.is_recurring && (
                              <>
                                <p className="text-[10px] text-gray-500 px-2 pb-1 font-semibold uppercase tracking-wider">Repeat...</p>
                                {[4, 8, 12].map(w => (
                                  <button key={w} onClick={() => makeRecurring(s.slot_id, w)}
                                    className="w-full text-left px-2 py-1.5 text-xs text-blue-400 hover:bg-blue-500/10 rounded-lg transition-colors"
                                    data-testid={`make-recurring-${w}w`}>
                                    <Calendar className="w-3 h-3 inline mr-1.5" />Make recurring for {w} weeks
                                  </button>
                                ))}
                                <div className="border-t border-border my-1" />
                              </>
                            )}
                            <p className="text-[10px] text-gray-500 px-2 pb-1 font-semibold uppercase tracking-wider">Remove...</p>
                            {[
                              { scope: "just_this", label: "Just this slot" },
                              ...(s.is_recurring || s.recurrence_group ? [{ scope: "all_recurring", label: "All recurring slots" }] : []),
                              { scope: "all_for_day", label: `All slots on ${date.slice(5)}` },
                              ...(s.session_type_id ? [{ scope: "all_of_type", label: `All "${st?.name || "this type"}" slots` }] : []),
                            ].map(opt => (
                              <button key={opt.scope} onClick={() => bulkAction(s.slot_id, "delete", opt.scope)}
                                className="w-full text-left px-2 py-1.5 text-xs text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
                                data-testid={`bulk-delete-${opt.scope}`}>
                                <Trash2 className="w-3 h-3 inline mr-1.5" />{opt.label}
                              </button>
                            ))}
                            <div className="border-t border-border my-1" />
                            <button onClick={() => setBulkTarget(null)} className="w-full text-left px-2 py-1.5 text-xs text-gray-400 hover:bg-white/5 rounded-lg">Cancel</button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                  {daySlots.length === 0 && <p className="text-xs text-white/20 text-center pt-4">No slots</p>}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

// ─── Sub-tab: Booking Requests ───
const BookingRequests = ({ bookings, onRefresh }) => {
  const update = async (id, status) => {
    const res = await fetch(`${API}/schedule/bookings/${id}`, { method: "PUT", headers: { "Content-Type": "application/json" }, credentials: "include", body: JSON.stringify({ status }) });
    if (res.ok) {
      const data = await res.json();
      if (status === "confirmed" && data.session_deducted) {
        const remaining = data.sessions_remaining;
        if (remaining !== null && remaining <= 3 && remaining > 0) {
          toast.warning(`${data.deducted_from}: only ${remaining} session${remaining === 1 ? "" : "s"} remaining!`, { duration: 6000 });
        } else if (remaining === 0) {
          toast.error(`${data.deducted_from}: all sessions used up! Athlete needs to purchase a new package.`, { duration: 8000 });
        }
        toast.success(`Booking confirmed — 1 session deducted from "${data.deducted_from}"`);
      } else if (status === "confirmed") {
        toast.success("Booking confirmed (no package to deduct from)");
      } else {
        toast.success(`Booking ${status}`);
      }
      onRefresh();
    }
  };

  const markAttendance = async (id, attendance) => {
    const res = await fetch(`${API}/schedule/bookings/${id}/attendance`, {
      method: "PUT", headers: { "Content-Type": "application/json" }, credentials: "include",
      body: JSON.stringify({ attendance }),
    });
    if (res.ok) { toast.success(`Marked as ${attendance === "attended" ? "Attended" : "No-Show"}`); onRefresh(); }
  };

  const pending = bookings.filter(b => b.status === "pending");
  const confirmed = bookings.filter(b => b.status === "confirmed");

  return (
    <div className="space-y-6">
      <div>
        <h4 className="font-semibold text-sm mb-3 text-amber-400">Pending Requests ({pending.length})</h4>
        {pending.length === 0 && <p className="text-text-muted text-sm">No pending requests</p>}
        <div className="space-y-2">
          {pending.map(b => (
            <div key={b.booking_id} className="bg-card border border-amber-500/20 rounded-xl p-4 flex items-center justify-between" data-testid={`booking-${b.booking_id}`}>
              <div>
                <p className="font-semibold text-white">{b.athlete_name}</p>
                <p className="text-xs text-text-secondary">{b.date} at {formatTime(b.start_time)} — {b.session_type_name}</p>
                {b.note && <p className="text-xs text-text-muted mt-1">"{b.note}"</p>}
                {b.is_recurring && <span className="text-[10px] bg-blue-500/20 text-blue-400 px-2 py-0.5 rounded-full">Recurring</span>}
              </div>
              <div className="flex gap-2">
                <button onClick={() => update(b.booking_id, "confirmed")} className="bg-emerald-600 text-white p-2 rounded-lg" data-testid={`confirm-${b.booking_id}`}><Check className="w-4 h-4" /></button>
                <button onClick={() => update(b.booking_id, "declined")} className="bg-red-600/80 text-white p-2 rounded-lg" data-testid={`decline-${b.booking_id}`}><X className="w-4 h-4" /></button>
              </div>
            </div>
          ))}
        </div>
      </div>
      <div>
        <h4 className="font-semibold text-sm mb-3 text-emerald-400">Confirmed ({confirmed.length})</h4>
        {confirmed.length === 0 && <p className="text-text-muted text-sm">No confirmed sessions</p>}
        <div className="space-y-2">
          {confirmed.map(b => (
            <div key={b.booking_id} className="bg-card border border-emerald-500/20 rounded-xl p-4" data-testid={`confirmed-${b.booking_id}`}>
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-semibold text-white">{b.athlete_name}</p>
                  <p className="text-xs text-text-secondary">{b.date} at {formatTime(b.start_time)} — {b.session_type_name}</p>
                  {b.session_deducted && <p className="text-[10px] text-emerald-400/70 mt-0.5">Session deducted from: {b.package_name}</p>}
                </div>
                <div className="flex items-center gap-2">
                  {b.attendance ? (
                    <span className={`text-[10px] px-2 py-1 rounded-full font-medium ${b.attendance === "attended" ? "bg-emerald-500/20 text-emerald-400" : "bg-red-500/20 text-red-400"}`}>
                      {b.attendance === "attended" ? "Attended" : "No-Show"}
                    </span>
                  ) : (
                    <>
                      <button onClick={() => markAttendance(b.booking_id, "attended")} className="bg-emerald-600/20 text-emerald-400 px-2 py-1 rounded-lg text-[10px] font-medium hover:bg-emerald-600/30" data-testid={`attend-${b.booking_id}`}>
                        <ClipboardCheck className="w-3 h-3 inline mr-1" />Attended
                      </button>
                      <button onClick={() => markAttendance(b.booking_id, "no_show")} className="bg-red-600/20 text-red-400 px-2 py-1 rounded-lg text-[10px] font-medium hover:bg-red-600/30" data-testid={`noshow-${b.booking_id}`}>
                        No-Show
                      </button>
                    </>
                  )}
                  <button onClick={() => update(b.booking_id, "cancelled")} className="text-red-400/50 hover:text-red-400 text-xs ml-1">Cancel</button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// ─── Sub-tab: Packages ───
const PackagesManager = ({ packages, onRefresh }) => {
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: "", description: "", package_type: "sessions", sessions_included: 10, price: 0, duration_label: "10-Pack" });

  const save = async () => {
    if (!form.name) return toast.error("Name required");
    const res = await fetch(`${API}/schedule/packages`, { method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include", body: JSON.stringify(form) });
    if (res.ok) { toast.success("Package created"); setShowForm(false); setForm({ name: "", description: "", package_type: "sessions", sessions_included: 10, price: 0, duration_label: "10-Pack" }); onRefresh(); }
  };

  const remove = async (id) => {
    await fetch(`${API}/schedule/packages/${id}`, { method: "DELETE", credentials: "include" });
    toast.success("Deleted"); onRefresh();
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-lg">Training Packages</h3>
        <button onClick={() => setShowForm(!showForm)} className="flex items-center gap-2 bg-primary text-white px-4 py-2 rounded-lg text-sm font-medium" data-testid="add-package-btn">
          <Plus className="w-4 h-4" /> Add Package
        </button>
      </div>

      {showForm && (
        <div className="bg-card border border-border rounded-xl p-4 space-y-3" data-testid="package-form">
          <input placeholder="Package name (e.g., 10-Session Pack)" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full bg-[#0B0F1A] border border-border rounded-lg px-3 py-2 text-white text-sm" data-testid="package-name" />
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <select value={form.package_type} onChange={(e) => setForm({ ...form, package_type: e.target.value })} className="bg-[#0B0F1A] border border-border rounded-lg px-3 py-2 text-white text-sm">
              <option value="sessions">Session Pack</option>
              <option value="monthly">Monthly</option>
            </select>
            <input type="number" placeholder="Sessions included" value={form.sessions_included} onChange={(e) => setForm({ ...form, sessions_included: parseInt(e.target.value) || 0 })} className="bg-[#0B0F1A] border border-border rounded-lg px-3 py-2 text-white text-sm" />
            <input type="number" placeholder="Price ($)" value={form.price} onChange={(e) => setForm({ ...form, price: parseFloat(e.target.value) || 0 })} className="bg-[#0B0F1A] border border-border rounded-lg px-3 py-2 text-white text-sm" />
            <input placeholder="Label (e.g., Per Month)" value={form.duration_label} onChange={(e) => setForm({ ...form, duration_label: e.target.value })} className="bg-[#0B0F1A] border border-border rounded-lg px-3 py-2 text-white text-sm" />
          </div>
          <textarea placeholder="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="w-full bg-[#0B0F1A] border border-border rounded-lg px-3 py-2 text-white text-sm h-16 resize-none" />
          <div className="flex gap-2">
            <button onClick={save} className="bg-primary text-white px-4 py-2 rounded-lg text-sm font-medium" data-testid="save-package">Save</button>
            <button onClick={() => setShowForm(false)} className="bg-[#1E293B] text-gray-400 px-4 py-2 rounded-lg text-sm">Cancel</button>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {packages.map((pkg) => (
          <div key={pkg.package_id} className="bg-card border border-border rounded-xl p-4 flex items-start justify-between" data-testid={`package-${pkg.package_id}`}>
            <div>
              <p className="font-semibold text-white">{pkg.name}</p>
              <div className="flex items-center gap-3 mt-1 text-xs text-text-secondary">
                <span className="bg-emerald-500/20 text-emerald-400 px-2 py-0.5 rounded-full">{pkg.package_type === "monthly" ? "Monthly" : "Session Pack"}</span>
                <span>{pkg.sessions_included} sessions</span>
                <span className="text-emerald-400 font-semibold">${pkg.price}</span>
                {pkg.duration_label && <span className="text-text-muted">{pkg.duration_label}</span>}
              </div>
              {pkg.description && <p className="text-xs text-text-muted mt-1">{pkg.description}</p>}
            </div>
            <button onClick={() => remove(pkg.package_id)} className="text-red-400/50 hover:text-red-400 p-1"><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
        {packages.length === 0 && <p className="text-text-muted text-sm col-span-2">No packages yet. Create your first one above.</p>}
      </div>
    </div>
  );
};

// ─── Sub-tab: Athlete Balances ───
const AthleteBalances = () => {
  const [balances, setBalances] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/schedule/athlete-balances`, { credentials: "include" });
        if (res.ok) setBalances((await res.json()).balances || []);
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    })();
  }, []);

  if (loading) return <div className="animate-pulse h-32 bg-card rounded-xl" />;

  const lowAthletes = balances.filter(a => a.total_remaining <= 3 && a.total_remaining > 0);
  const zeroAthletes = balances.filter(a => a.total_remaining === 0);

  const getBadge = (remaining) => {
    if (remaining === 0) return { bg: "bg-red-500/20", text: "text-red-400", label: "0 left" };
    if (remaining === 1) return { bg: "bg-red-500/20", text: "text-red-400", label: "1 left" };
    if (remaining === 2) return { bg: "bg-orange-500/20", text: "text-orange-400", label: "2 left" };
    if (remaining === 3) return { bg: "bg-amber-500/20", text: "text-amber-400", label: "3 left" };
    return { bg: "bg-emerald-500/20", text: "text-emerald-400", label: `${remaining} left` };
  };

  return (
    <div className="space-y-6" data-testid="athlete-balances">
      {/* Alerts */}
      {(zeroAthletes.length > 0 || lowAthletes.length > 0) && (
        <div className="space-y-2">
          {zeroAthletes.map(a => (
            <div key={a.user_id} className="flex items-center gap-3 bg-red-500/10 border border-red-500/30 rounded-xl p-3" data-testid={`alert-zero-${a.user_id}`}>
              <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0" />
              <p className="text-sm text-red-300"><span className="font-semibold text-red-400">{a.user_name || a.user_email}</span> has used all sessions. Needs a new package.</p>
            </div>
          ))}
          {lowAthletes.map(a => (
            <div key={a.user_id} className={`flex items-center gap-3 rounded-xl p-3 ${a.total_remaining === 1 ? "bg-red-500/10 border border-red-500/30" : a.total_remaining === 2 ? "bg-orange-500/10 border border-orange-500/30" : "bg-amber-500/10 border border-amber-500/30"}`} data-testid={`alert-low-${a.user_id}`}>
              <AlertTriangle className={`w-5 h-5 flex-shrink-0 ${a.total_remaining === 1 ? "text-red-400" : a.total_remaining === 2 ? "text-orange-400" : "text-amber-400"}`} />
              <p className={`text-sm ${a.total_remaining === 1 ? "text-red-300" : a.total_remaining === 2 ? "text-orange-300" : "text-amber-300"}`}>
                <span className="font-semibold">{a.user_name || a.user_email}</span> — only {a.total_remaining} session{a.total_remaining === 1 ? "" : "s"} remaining
              </p>
            </div>
          ))}
        </div>
      )}

      {/* All athletes */}
      {balances.length === 0 ? (
        <div className="text-center py-12 text-text-muted">
          <UserCheck className="w-8 h-8 mx-auto mb-2 opacity-50" />
          <p className="text-sm">No athletes have purchased packages yet.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {balances.map(a => {
            const badge = getBadge(a.total_remaining);
            return (
              <div key={a.user_id} className="bg-card border border-border rounded-xl p-4" data-testid={`athlete-balance-${a.user_id}`}>
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <p className="font-semibold text-white">{a.user_name || "Unnamed"}</p>
                    <p className="text-xs text-text-muted">{a.user_email}</p>
                  </div>
                  <span className={`${badge.bg} ${badge.text} text-xs font-bold px-3 py-1 rounded-full`}>{badge.label}</span>
                </div>
                <div className="flex gap-2 flex-wrap">
                  {a.packages.map(pkg => (
                    <div key={pkg.purchase_id} className="bg-[#0B0F1A] border border-border rounded-lg px-3 py-1.5 text-xs">
                      <span className="text-text-secondary">{pkg.package_name}</span>
                      <span className={`ml-2 font-bold ${pkg.sessions_remaining <= 1 ? "text-red-400" : pkg.sessions_remaining <= 3 ? "text-amber-400" : "text-emerald-400"}`}>{pkg.sessions_remaining}/{pkg.sessions_included}</span>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ─── Sub-tab: Session History ───
const SessionHistory = () => {
  const [data, setData] = useState({ sessions: [], stats: {} });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/schedule/session-history`, { credentials: "include" });
        if (res.ok) setData(await res.json());
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    })();
  }, []);

  if (loading) return <div className="animate-pulse h-32 bg-card rounded-xl" />;

  const { sessions, stats } = data;

  return (
    <div className="space-y-4" data-testid="session-history">
      {/* Stats row */}
      <div className="grid grid-cols-4 gap-3">
        {[
          { label: "Total", value: stats.total || 0, color: "text-white" },
          { label: "Attended", value: stats.attended || 0, color: "text-emerald-400" },
          { label: "No-Show", value: stats.no_show || 0, color: "text-red-400" },
          { label: "Unmarked", value: stats.unmarked || 0, color: "text-amber-400" },
        ].map(s => (
          <div key={s.label} className="bg-card border border-border rounded-xl p-3 text-center">
            <p className={`text-xl font-bold ${s.color}`}>{s.value}</p>
            <p className="text-[10px] text-text-muted">{s.label}</p>
          </div>
        ))}
      </div>

      {sessions.length === 0 ? (
        <p className="text-text-muted text-sm text-center py-8">No session history yet.</p>
      ) : (
        <div className="space-y-2">
          {sessions.map(s => (
            <div key={s.booking_id} className="bg-card border border-border rounded-xl p-3 flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-white">{s.athlete_name}</p>
                <p className="text-xs text-text-muted">{s.date} at {formatTime(s.start_time)} — {s.session_type_name}</p>
              </div>
              <span className={`text-[10px] px-2 py-1 rounded-full font-medium ${
                s.attendance === "attended" ? "bg-emerald-500/20 text-emerald-400" :
                s.attendance === "no_show" ? "bg-red-500/20 text-red-400" :
                "bg-gray-500/20 text-gray-400"
              }`}>
                {s.attendance === "attended" ? "Attended" : s.attendance === "no_show" ? "No-Show" : "Unmarked"}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ─── Sub-tab: Schedule Settings ───
const ScheduleSettings = () => {
  const [policy, setPolicy] = useState({ cancel_window_hours: 0, late_cancel_deducts: true });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      const res = await fetch(`${API}/schedule/settings/cancellation-policy`, { credentials: "include" });
      if (res.ok) setPolicy(await res.json());
    })();
  }, []);

  const save = async () => {
    setSaving(true);
    const res = await fetch(`${API}/schedule/settings/cancellation-policy`, {
      method: "PUT", headers: { "Content-Type": "application/json" }, credentials: "include",
      body: JSON.stringify(policy),
    });
    if (res.ok) toast.success("Cancellation policy saved");
    else toast.error("Failed to save");
    setSaving(false);
  };

  const sendReminders = async () => {
    const res = await fetch(`${API}/schedule/send-reminders`, { method: "POST", credentials: "include" });
    if (res.ok) {
      const data = await res.json();
      toast.success(`${data.reminders_sent} reminder${data.reminders_sent !== 1 ? "s" : ""} sent (${data.total_tomorrow} sessions tomorrow)`);
    }
  };

  return (
    <div className="space-y-6" data-testid="schedule-settings">
      {/* Cancellation Policy */}
      <div className="bg-card border border-border rounded-xl p-5 space-y-4">
        <h4 className="font-semibold text-white text-sm flex items-center gap-2"><Settings className="w-4 h-4 text-primary" /> Cancellation Policy</h4>
        <div className="space-y-3">
          <div>
            <label className="block text-xs text-text-muted mb-1">Cancellation Window (hours before session)</label>
            <select
              value={policy.cancel_window_hours}
              onChange={(e) => setPolicy(p => ({ ...p, cancel_window_hours: parseInt(e.target.value) }))}
              className="bg-[#0B0F1A] border border-border rounded-lg px-3 py-2 text-white text-sm w-full"
              data-testid="cancel-window-select"
            >
              <option value={0}>No restriction (free cancellation)</option>
              <option value={6}>6 hours</option>
              <option value={12}>12 hours</option>
              <option value={24}>24 hours</option>
              <option value={48}>48 hours</option>
            </select>
          </div>
          <label className="flex items-center gap-2 text-sm text-text-secondary cursor-pointer">
            <input type="checkbox" checked={policy.late_cancel_deducts}
              onChange={(e) => setPolicy(p => ({ ...p, late_cancel_deducts: e.target.checked }))}
              className="rounded" data-testid="late-cancel-deducts" />
            Late cancellations still deduct a session from the athlete's package
          </label>
          <button onClick={save} disabled={saving}
            className="bg-primary text-white px-5 py-2 rounded-lg text-sm font-medium hover:bg-primary/90 disabled:opacity-50 transition-colors"
            data-testid="save-cancel-policy"
          >
            {saving ? "Saving..." : "Save Policy"}
          </button>
        </div>
      </div>

      {/* Send Reminders */}
      <div className="bg-card border border-border rounded-xl p-5 space-y-3">
        <h4 className="font-semibold text-white text-sm flex items-center gap-2"><Bell className="w-4 h-4 text-amber-400" /> Session Reminders</h4>
        <p className="text-xs text-text-muted">Send email reminders to all athletes with confirmed sessions tomorrow.</p>
        <button onClick={sendReminders}
          className="flex items-center gap-2 bg-amber-600 text-white px-5 py-2 rounded-lg text-sm font-medium hover:bg-amber-500 transition-colors"
          data-testid="send-reminders-btn"
        >
          <Send className="w-4 h-4" /> Send Tomorrow's Reminders
        </button>
      </div>
    </div>
  );
};

// ─── MAIN COMPONENT ───
const CoachSchedule = () => {
  const [subTab, setSubTab] = useState("availability");
  const [sessionTypes, setSessionTypes] = useState([]);
  const [slots, setSlots] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [packages, setPackages] = useState([]);
  const [athleteBalances, setAthleteBalances] = useState([]);

  const fetchAll = useCallback(async () => {
    const [stRes, slRes, bkRes, pkRes, abRes] = await Promise.all([
      fetch(`${API}/schedule/session-types`, { credentials: "include" }),
      fetch(`${API}/schedule/availability`, { credentials: "include" }),
      fetch(`${API}/schedule/bookings`, { credentials: "include" }),
      fetch(`${API}/schedule/packages`, { credentials: "include" }),
      fetch(`${API}/schedule/athlete-balances`, { credentials: "include" }),
    ]);
    if (stRes.ok) setSessionTypes((await stRes.json()).session_types || []);
    if (slRes.ok) setSlots((await slRes.json()).slots || []);
    if (bkRes.ok) setBookings((await bkRes.json()).bookings || []);
    if (pkRes.ok) setPackages((await pkRes.json()).packages || []);
    if (abRes.ok) setAthleteBalances((await abRes.json()).balances || []);
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const pendingCount = bookings.filter(b => b.status === "pending").length;
  const lowSessionCount = athleteBalances.filter(a => a.total_remaining > 0 && a.total_remaining <= 3).length;

  const subTabs = [
    { id: "availability", label: "Availability", icon: Calendar },
    { id: "session-types", label: "Session Types", icon: Clock },
    { id: "requests", label: "Requests", icon: Users, badge: pendingCount },
    { id: "packages", label: "Packages", icon: Package },
    { id: "athletes", label: "Athletes", icon: UserCheck, badge: lowSessionCount },
    { id: "history", label: "History", icon: ClipboardCheck },
    { id: "settings", label: "Settings", icon: Settings },
  ];

  return (
    <div className="space-y-6" data-testid="coach-schedule">
      {/* Sub-tabs */}
      <div className="flex gap-2 overflow-x-auto scrollbar-hide -mx-4 px-4 sm:mx-0 sm:px-0">
        {subTabs.map(tab => (
          <button key={tab.id} onClick={() => setSubTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${subTab === tab.id ? "bg-primary text-white" : "bg-[#1E293B] text-text-secondary hover:text-white"}`}
            data-testid={`schedule-tab-${tab.id}`}>
            <tab.icon className="w-4 h-4" /> {tab.label}
            {tab.badge > 0 && <span className={`${tab.id === "athletes" ? "bg-amber-500" : "bg-red-500"} text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full`}>{tab.badge}</span>}
          </button>
        ))}
      </div>

      {subTab === "availability" && <AvailabilityManager slots={slots} sessionTypes={sessionTypes} onRefresh={fetchAll} />}
      {subTab === "session-types" && <SessionTypesManager sessionTypes={sessionTypes} onRefresh={fetchAll} />}
      {subTab === "requests" && <BookingRequests bookings={bookings} onRefresh={fetchAll} />}
      {subTab === "packages" && <PackagesManager packages={packages} onRefresh={fetchAll} />}
      {subTab === "athletes" && <AthleteBalances />}
      {subTab === "history" && <SessionHistory />}
      {subTab === "settings" && <ScheduleSettings />}
    </div>
  );
};

export default CoachSchedule;
